import React from 'react';
import { Button, Input, Table, TagPicker, Tooltip, Whisper } from 'rsuite';
import * as studentsApi from '../../api/students';
import * as coursesApi from '../../api/courses';
import EditIcon from '@rsuite/icons/Edit';
import DeleteIcon from '@rsuite/icons/Trash';
import EditStudentModal from './EditStudentModal';
import StudentQueuesModal from './StudentQueuesModal';
import CreateStudentModal from './CreateStudentModal';
import PageLayout from '../../common/PageLayout';
import ColumnsButton from '../../common/ColumnsButton';
import FilterPanel, { FilterField } from '../../common/FilterPanel';
import { useTableColumns } from '../../common/useTableColumns';
import { useResourceList } from '../../common/useResourceList';
import { truncatedTextCell } from '../../common/tableCells';
import { formatDate, getRowClassName } from '../../common/formatters';
import { renderRowNumberColumn } from '../../common/RowNumberColumn';
import { renderTableEmpty, renderTableLoading } from '../../common/TableStates';
import TablePagination from '../../common/TablePagination';
import { useConfirm } from '../../common/useConfirm';

const { Column, HeaderCell, Cell } = Table;

const STUDENTS_EMAIL_PREVIEW_MAX_LENGTH = 40;
const STUDENTS_TEXT_PREVIEW_MAX_LENGTH = 72;
const STUDENTS_LIST_PREVIEW_MAX_ITEMS = 2;

const TABLE_COLUMNS = [
  { value: 'email', label: 'Пошта', display: true, width: 260, minWidth: 220 },
  { value: 'typeName', label: 'Тип', display: true, width: 140, minWidth: 120 },
  {
    value: 'firstName',
    label: "Ім'я",
    display: true,
    width: 150,
    minWidth: 130,
  },
  {
    value: 'secondName',
    label: 'Прізвище',
    display: true,
    width: 170,
    minWidth: 150,
  },
  {
    value: 'username',
    label: 'Логін',
    display: true,
    width: 160,
    minWidth: 140,
  },
  {
    value: 'phone',
    label: 'Телефон',
    display: true,
    width: 170,
    minWidth: 150,
  },
  {
    value: 'data',
    label: 'Коментарі',
    display: true,
    width: 220,
    minWidth: 180,
  },
  {
    value: 'queueEntries',
    label: 'У черзі на курси',
    display: true,
    width: 230,
    minWidth: 200,
  },
  {
    value: 'groups',
    label: 'У групах',
    display: true,
    width: 220,
    minWidth: 190,
  },
  {
    value: 'createdAt',
    label: 'Створено',
    display: false,
    width: 140,
    minWidth: 120,
  },
  {
    value: 'updatedAt',
    label: 'Оновлено',
    display: true,
    width: 140,
    minWidth: 120,
  },
];

type StudentRow = {
  id: string;
  email: string;
  type: number;
  typeName: string;
  firstName: string;
  secondName: string;
  username: string;
  phone: string;
  data: string;
  queueEntries: unknown[];
  groups: unknown[];
  createdAt: number;
  updatedAt: number;
};

export default function Students() {
  const [confirm, ConfirmDialog] = useConfirm();
  const clickedCellRef = React.useRef(false);
  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [secondName, setSecondName] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [coursesData, setCoursesData] = React.useState<
    { value: string; label: string }[]
  >([]);
  const [typesData, setTypesData] = React.useState<
    { value: number; label: string }[]
  >([]);
  const [queueCourseIds, setQueueCourseIds] = React.useState<string[]>([]);
  const [groupsCourseIds, setGroupsCourseIds] = React.useState<string[]>([]);
  const [addToQueueStudentId, setAddToQueueStudentId] =
    React.useState<string>('');

  const [tableColumns, , toggleColumn] = useTableColumns(
    TABLE_COLUMNS,
    'students:tableColumns'
  );

  const [updateModalObject, setUpdateModalObject] = React.useState<any | null>(
    null
  );
  const [createModalOpen, setCreateModalOpen] = React.useState<boolean>(false);

  const list = useResourceList<StudentRow>({
    fetch: ({ page, perPage, sortColumn, sortOrder }) =>
      studentsApi.list(
        page,
        perPage,
        email || undefined,
        name,
        secondName,
        username,
        phone,
        queueCourseIds,
        groupsCourseIds,
        sortColumn,
        sortOrder
      ),
    filters: [
      email,
      name,
      secondName,
      username,
      phone,
      queueCourseIds,
      groupsCourseIds,
    ],
    initialSortColumn: 'createdAt',
  });

  const getTagsPickersData = async () => {
    const { courses: coursesResponse } = await coursesApi.list(
      'updatedAt',
      'DESC'
    );
    const { types: typesResponse } = await studentsApi.getTypes();
    setCoursesData(
      coursesResponse.map((c: any) => ({ value: c.id, label: c.name }))
    );
    setTypesData(typesResponse);
  };

  React.useEffect(() => {
    getTagsPickersData().catch(console.error);
  }, []);

  const firstVisibleColumnValue = React.useMemo(
    () => tableColumns.find(column => column.display !== false)?.value,
    [tableColumns]
  );

  const renderQueueEntriesSummary = React.useCallback((queueEntries: any[]) => {
    const names = (queueEntries || [])
      .map(qE => qE?.course?.name)
      .filter(Boolean) as string[];

    if (!names.length) {
      return '-';
    }

    const previewNames = names.slice(0, STUDENTS_LIST_PREVIEW_MAX_ITEMS);
    const remaining = names.length - previewNames.length;
    const previewText =
      remaining > 0
        ? `${previewNames.join(', ')} +${remaining}`
        : previewNames.join(', ');

    return (
      <Whisper
        placement="top"
        trigger="hover"
        speaker={<Tooltip>{names.join(', ')}</Tooltip>}
      >
        <span className="requests-table__cell-text">{previewText}</span>
      </Whisper>
    );
  }, []);

  const renderGroupsSummary = React.useCallback((groups: any[]) => {
    const sortedGroups = [...(groups || [])].sort(
      (a, b) => Number(b.createdAt) - Number(a.createdAt)
    );
    const names = sortedGroups
      .map(group => group?.name)
      .filter(Boolean) as string[];

    if (!names.length) {
      return '-';
    }

    const previewNames = names.slice(0, STUDENTS_LIST_PREVIEW_MAX_ITEMS);
    const remaining = names.length - previewNames.length;
    const previewText =
      remaining > 0
        ? `${previewNames.join(', ')} +${remaining}`
        : previewNames.join(', ');

    return (
      <Whisper
        trigger="hover"
        placement="auto"
        speaker={
          <Tooltip>
            {sortedGroups.map((group: any) => (
              <div key={group.id}>
                {group.name}
                {group.finishedAt
                  ? ` (завершена ${formatDate(group.finishedAt)})`
                  : ' (в процесі)'}
              </div>
            ))}
          </Tooltip>
        }
      >
        <span className="requests-table__cell-text">{previewText}</span>
      </Whisper>
    );
  }, []);

  const rowHeight = 52;
  const headerHeight = 54;

  // Email is the always-visible search; other dimensions are filters.
  const activeFilterCount =
    [name, secondName, username, phone].filter(Boolean).length +
    [queueCourseIds, groupsCourseIds].filter(arr => arr.length > 0).length;

  const resetFilters = () => {
    setName('');
    setSecondName('');
    setUsername('');
    setPhone('');
    setQueueCourseIds([]);
    setGroupsCourseIds([]);
  };

  return (
    <>
      {ConfirmDialog}
      <PageLayout
        title="Студенти"
        actions={
          <>
            <Input
              className="toolbar-search"
              placeholder="Пошук за поштою"
              value={email || ''}
              onChange={setEmail}
            />
            <FilterPanel activeCount={activeFilterCount} onReset={resetFilters}>
              {filterContainer => (
                <>
                  <FilterField label="Ім'я">
                    <Input
                      placeholder="Ім'я"
                      value={name || ''}
                      onChange={setName}
                    />
                  </FilterField>
                  <FilterField label="Прізвище">
                    <Input
                      placeholder="Прізвище"
                      value={secondName || ''}
                      onChange={setSecondName}
                    />
                  </FilterField>
                  <FilterField label="Логін">
                    <Input
                      placeholder="Логін"
                      value={username || ''}
                      onChange={setUsername}
                    />
                  </FilterField>
                  <FilterField label="Телефон">
                    <Input
                      placeholder="Телефон"
                      value={phone || ''}
                      onChange={setPhone}
                    />
                  </FilterField>
                  <FilterField
                    label="Курси на які очікують"
                    hint={
                      queueCourseIds.length > 0
                        ? 'У студентів відобразяться лише ті курси, що відмічені у фільтрі.'
                        : undefined
                    }
                  >
                    <TagPicker
                      block
                      placeholder="Курси на які очікують"
                      value={queueCourseIds}
                      onChange={setQueueCourseIds}
                      data={coursesData}
                      container={filterContainer}
                      preventOverflow
                    />
                  </FilterField>
                  <FilterField label="Курси у групах">
                    <TagPicker
                      block
                      placeholder="Курси у групах"
                      value={groupsCourseIds}
                      onChange={setGroupsCourseIds}
                      data={coursesData}
                      container={filterContainer}
                      preventOverflow
                    />
                  </FilterField>
                </>
              )}
            </FilterPanel>
            <ColumnsButton columns={tableColumns} onToggle={toggleColumn} />
            <Button
              appearance="primary"
              onClick={() => setCreateModalOpen(true)}
            >
              Створити
            </Button>
          </>
        }
      >
        <div className="section-card">
          <Table
            style={{ width: '100%' }}
            className="requests-table"
            wordWrap="keep-all"
            loading={list.loading}
            autoHeight={true}
            rowHeight={rowHeight}
            headerHeight={headerHeight}
            affixHeader={true}
            affixHorizontalScrollbar={true}
            bordered={true}
            data={list.data}
            renderLoading={renderTableLoading}
            renderEmpty={renderTableEmpty}
            rowClassName={getRowClassName}
            onRowClick={rowData => {
              if (clickedCellRef.current) {
                clickedCellRef.current = false;
                return;
              }
              setUpdateModalObject(rowData);
            }}
            sortColumn={list.sortColumn}
            sortType={list.sortType}
            onSortColumn={(sortColumn, sortType) =>
              list.setSort(sortColumn, sortType || 'desc')
            }
          >
            {renderRowNumberColumn({ page: list.page, perPage: list.perPage })}

            {tableColumns.map(column => {
              if (column.display === false) {
                return null;
              }
              return (
                <Column
                  key={column.value}
                  fullText={true}
                  width={column.width}
                  minWidth={column.minWidth || 80}
                  fixed={
                    column.value === firstVisibleColumnValue
                      ? ('left' as const)
                      : undefined
                  }
                  sortable={true}
                >
                  <HeaderCell>{column.label}</HeaderCell>
                  {['updatedAt', 'createdAt'].includes(column.value) ? (
                    <Cell
                      dataKey={column.value}
                      className="requests-cell-nowrap"
                    >
                      {rowData => formatDate(rowData[column.value])}
                    </Cell>
                  ) : column.value === 'groups' ? (
                    <Cell
                      dataKey={column.value}
                      className="requests-cell-left requests-cell-nowrap requests-cell-ellipsis"
                    >
                      {rowData => renderGroupsSummary(rowData.groups)}
                    </Cell>
                  ) : column.value === 'queueEntries' ? (
                    <Cell
                      dataKey={column.value}
                      className="requests-cell-left requests-cell-nowrap requests-cell-ellipsis"
                    >
                      {rowData =>
                        renderQueueEntriesSummary(rowData.queueEntries)
                      }
                    </Cell>
                  ) : [
                      'email',
                      'firstName',
                      'secondName',
                      'username',
                      'phone',
                    ].includes(column.value) ? (
                    <Cell
                      dataKey={column.value}
                      className="requests-cell-left requests-cell-nowrap requests-cell-ellipsis"
                    >
                      {rowData =>
                        truncatedTextCell(rowData[column.value], {
                          maxLength:
                            column.value === 'email'
                              ? STUDENTS_EMAIL_PREVIEW_MAX_LENGTH
                              : STUDENTS_TEXT_PREVIEW_MAX_LENGTH,
                          stripTags: true,
                        })
                      }
                    </Cell>
                  ) : column.value === 'data' ? (
                    <Cell
                      dataKey={column.value}
                      className="requests-cell-left requests-cell-nowrap requests-cell-ellipsis"
                    >
                      {rowData =>
                        truncatedTextCell(rowData[column.value], {
                          maxLength: STUDENTS_TEXT_PREVIEW_MAX_LENGTH,
                          stripTags: true,
                        })
                      }
                    </Cell>
                  ) : (
                    <Cell dataKey={column.value} />
                  )}
                </Column>
              );
            })}

            <Column fixed="right" width={130}>
              <HeaderCell>Дії</HeaderCell>
              <Cell
                onClick={() => {
                  clickedCellRef.current = true;
                }}
                style={{ padding: '4px 6px' }}
              >
                {rowData => (
                  <div className="action-icons">
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Додати в чергу</Tooltip>}
                    >
                      <Button
                        appearance="link"
                        onClick={() => setAddToQueueStudentId(rowData.id)}
                      >
                        <img width={18} src="/add_to_list.svg" alt="Черга" />
                      </Button>
                    </Whisper>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Редагувати</Tooltip>}
                    >
                      <Button
                        appearance="link"
                        onClick={() => setUpdateModalObject(rowData)}
                      >
                        <EditIcon />
                      </Button>
                    </Whisper>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Видалити</Tooltip>}
                    >
                      <Button
                        appearance="subtle"
                        className="delete-btn"
                        onClick={async () => {
                          const result = await confirm({
                            message: `Ви впевнені, що хочете видалити студента ${rowData.firstName} ${rowData.secondName} (${rowData.email})?`,
                            destructive: true,
                            confirmText: 'Видалити',
                          });
                          if (!result) {
                            return;
                          }
                          studentsApi
                            .remove(rowData.id)
                            .catch(console.error)
                            .then(() => list.reload());
                        }}
                      >
                        <DeleteIcon />
                      </Button>
                    </Whisper>
                  </div>
                )}
              </Cell>
            </Column>
          </Table>
          <TablePagination
            total={list.totalAmount}
            activePage={list.page}
            pages={list.totalPages}
            limit={list.perPage}
            limitOptions={[15, 30, 50, 100]}
            onChangeLimit={list.setPerPage}
            onChangePage={list.goToPage}
          />
        </div>
      </PageLayout>

      <CreateStudentModal
        open={createModalOpen}
        setOpen={setCreateModalOpen}
        coursesData={coursesData}
        typesData={typesData}
        callback={() => {
          list.reload();
        }}
      />
      <StudentQueuesModal
        studentId={addToQueueStudentId}
        setStudentId={setAddToQueueStudentId}
        coursesData={coursesData}
      />
      <EditStudentModal
        obj={updateModalObject}
        coursesData={coursesData}
        typesData={typesData}
        setObj={setUpdateModalObject}
        callback={() => {
          list.reload();
        }}
      />
    </>
  );
}
